import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import AudioPlayer from '../../components/EditingSoftware/AudioPlayer';
import TranscriptViewer from '../../components/EditingSoftware/TranscriptViewer';
import Timeline from '../../components/EditingSoftware/Timeline';
import Header from '../../components/EditingSoftware/Header';
import VideoPlayer from '../../components/EditingSoftware/VideoPlayer';
import FlexibleLayout from '../../components/EditingSoftware/ModularLayout';
import { Box } from '@mui/material';
import { API_CONFIG } from '../../config/constants';
import UndoIcon from '@mui/icons-material/Undo';
import ReferenceFilesManager from '../../components/EditingSoftware/ReferenceFilesManager';
import AudioVolumePanel from '../../components/EditingSoftware/AudioVolumePanel';
import LoadingScreen from '../../components/Loading/LoadingScreen';
import { projectService } from '../../services/project.service';
import SubtitleManager from '../../components/EditingSoftware/SubtitleManager';
import LipSyncManager from '../../components/EditingSoftware/LipSyncManager';

function MainPage() {
    const [transcript, setTranscript] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [mutedTracks, setMutedTracks] = useState({});
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [audioSegments, setAudioSegments] = useState({});
    const [message, setMessage] = useState('');
    const [videoUrl, setVideoUrl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [subtitleSettings, setSubtitleSettings] = useState({
        enabled: true,
        font: 'Arial',
        fontSize: 24,
        color: '#FFFFFF',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'bottom',
        alignment: 'center',
        showBackground: true
    });
    const [subtitles, setSubtitles] = useState([]);

    const videoRef = useRef(null);
    const videoPlayerRef = useRef(null);
    const startTimeRef = useRef(0);
    const intervalRef = useRef(null);
    const [isVideoAudioEnabled, setIsVideoAudioEnabled] = useState(false);

    const [referenceFiles, setReferenceFiles] = useState([]);
    const [assetsLoaded, setAssetsLoaded] = useState(false);

    const [canUndo, setCanUndo] = useState(false);
    const [isUndoing, setIsUndoing] = useState(false);

    // Référence pour les AudioPlayers avec une structure plus claire
    const audioPlayerRefs = useRef({});

    const { projectId } = useParams();

    const [lipsyncProcessingInfo, setLipsyncProcessingInfo] = useState({
        processingSegments: new Map(),
        videoAudioSegments: []
    });

    const handleLipsyncProcessingChange = useCallback((processingSegments, videoAudioSegments) => {
        setLipsyncProcessingInfo({ processingSegments, videoAudioSegments });
    }, []);

    const fetchReferenceFiles = useCallback(async () => {
        try {
            const response = await fetch(
                `${API_CONFIG.BASE_URL}/projects/${projectId}/reference_files`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setReferenceFiles(data.reference_files);
            }
        } catch (error) {
            console.error('Error fetching reference files:', error);
        }
    }, [projectId]);

    const fetchTranscript = useCallback(async () => {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/get_file/${projectId}/merged_transcript_with_audio.json`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                params: { timestamp: new Date().getTime() }
            });

            // Extraire les segments du transcript
            const data = response.data.segments || response.data;

            // Ajout des trim_start/trim_end si nécessaire
            const processedData = data.map(item => {
                if (!item.trim_start) {
                    return {
                        ...item,
                        trim_start: item.start,
                        trim_end: item.end,
                    };
                }
                return item;
            });

            // Mise à jour du transcript
            setTranscript(processedData);

            // Calculer la durée totale
            const totalDuration = Math.max(...processedData.map(item => item.end));
            setDuration(totalDuration);

            // Récupérer tous les speakers (SPEAKER_X et BACKGROUND_X)
            const uniqueSpeakers = [...new Set(processedData.map(item => item.speaker))];

            // Initialiser les états de mute pour tous les speakers
            const muted = Object.fromEntries(uniqueSpeakers.map(speaker => [speaker, false]));
            setMutedTracks(muted);

            // Grouper les segments par speaker
            const segments = Object.fromEntries(uniqueSpeakers.map(speaker => [
                speaker,
                processedData.filter(item => item.speaker === speaker)
            ]));
            setAudioSegments(segments);

        } catch (error) {
            setMessage('Failed to fetch transcript: ' + (error.response?.data?.msg || error.message));
        }
    }, [projectId]);

    const fetchVideoUrl = useCallback(async () => {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/get_file/${projectId}/video.mp4`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            setVideoUrl(response.data?.url || null);
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Failed to fetch video URL:', error);
                setMessage('Failed to fetch video: ' + (error.response?.data?.msg || error.message));
            }
        }
    }, [projectId]);

    useEffect(() => {
        fetchTranscript();
        fetchVideoUrl();
        fetchReferenceFiles();  // Ajouté ici
        loadSubtitles();
    }, [fetchTranscript, fetchVideoUrl, fetchReferenceFiles]);

    const checkAllAssetsLoaded = useCallback(() => {
        if (!transcript.length) return false;

        // Vérifier si tous les AudioPlayers ont terminé leur préchargement
        const allAudioPlayersReady = Object.keys(audioPlayerRefs.current).every(speaker => {
            return audioPlayerRefs.current[speaker]?.isCacheReady?.();
        });

        // Le VideoPlayer gère son propre préchargement de manière autonome
        // On considère qu'il est toujours prêt car il a son propre système de cache
        // Si on veut vraiment vérifier son état, on peut ajouter une méthode séparée

        return allAudioPlayersReady;
    }, [transcript]);

    const checkVideoPlayerReady = useCallback(() => {
        // Vérifier si le VideoPlayer a une méthode pour indiquer qu'il est prêt
        return videoPlayerRef.current?.isCacheReady?.() ?? true; // true par défaut si pas de méthode
    }, []);

    // Nouveau: fonction pour charger les sous-titres
    const loadSubtitles = useCallback(async () => {
        try {
            const data = await projectService.getSubtitles(projectId);

            // S'assurer que tous les paramètres sont présents
            const completeSettings = {
                ...subtitleSettings,
                ...data.settings,
                // Ajouter showBackground s'il n'existe pas encore
                showBackground: data.settings.showBackground !== undefined
                    ? data.settings.showBackground
                    : data.settings.backgroundColor !== 'rgba(0, 0, 0, 0)'
            };

            // Mettre à jour les états
            setSubtitleSettings(completeSettings);
            setSubtitles(data.subtitles || []);

            console.log("Subtitle data loaded:", data);
        } catch (error) {
            console.error("Error loading subtitles:", error);
        }
    }, [projectId, subtitleSettings]);


    useEffect(() => {
        if (assetsLoaded) return;

        const isAudioReady = checkAllAssetsLoaded();
        const isVideoReady = checkVideoPlayerReady();

        if (isAudioReady && isVideoReady) {
            setAssetsLoaded(true);
            return;
        }

        const checkInterval = setInterval(() => {
            const isAudioReady = checkAllAssetsLoaded();
            const isVideoReady = checkVideoPlayerReady();

            if (isAudioReady && isVideoReady) {
                setAssetsLoaded(true);
                clearInterval(checkInterval);
            }
        }, 500);

        return () => clearInterval(checkInterval);
    }, [assetsLoaded, checkAllAssetsLoaded, checkVideoPlayerReady, transcript]);

    const updateTime = useCallback(() => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setCurrentTime(prevTime => {
            const newTime = prevTime + elapsed;
            if (newTime > duration) {
                setIsPlaying(false); // Arrêter la lecture à la fin
                return 0;
            }
            return newTime;
        });
        startTimeRef.current = Date.now();
    }, [duration]);

    useEffect(() => {
        if (isPlaying) {
            startTimeRef.current = Date.now();
            intervalRef.current = setInterval(updateTime, 100);
            videoRef.current?.play();
        } else {
            clearInterval(intervalRef.current);
            videoRef.current?.pause();
        }

        return () => clearInterval(intervalRef.current);
    }, [isPlaying, updateTime]);

    const handlePlayPause = useCallback(() => {
        setIsPlaying(prev => !prev);
    }, []);

    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.code === 'Space' && !['INPUT', 'TEXTAREA'].includes(event.target.tagName)) {
                event.preventDefault();
                handlePlayPause();
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [handlePlayPause]);

    const handleSeek = useCallback((newTime) => {
        const wasPlaying = isPlaying;
        if (wasPlaying) {
            setIsPlaying(false); // Mettre en pause pendant le seek
        }

        setCurrentTime(newTime);

        // Gérer la vidéo
        if (videoRef.current) {
            videoRef.current.currentTime = newTime;
        }

        startTimeRef.current = Date.now();

        // NE PAS manipuler directement les AudioPlayers ici!
        // Le changement de currentTime va déclencher les effets dans AudioPlayer
        // qui vont gérer automatiquement la lecture

        // Reprendre la lecture après un court délai
        if (wasPlaying) {
            setTimeout(() => {
                setIsPlaying(true);
            }, 100);
        }
    }, [isPlaying]);

    const handleSkip = useCallback((seconds) => {
        const newTime = Math.min(Math.max(currentTime + seconds, 0), duration);
        handleSeek(newTime);
    }, [currentTime, duration, handleSeek]);

    const toggleMute = useCallback((speaker) => {
        setMutedTracks(prevMutedTracks => {
            // ✅ CORRECTION : Calculer l'état actuel tel qu'il apparaît dans l'interface
            const currentEffectiveMuted = prevMutedTracks[speaker] || isVideoAudioEnabled;

            console.log(`[${speaker}] Toggle mute:`, {
                currentMutedTracks: prevMutedTracks[speaker],
                isVideoAudioEnabled,
                currentEffectiveMuted,
                willSetTo: !currentEffectiveMuted
            });

            if (currentEffectiveMuted) {
                // Si actuellement affiché comme muté, forcer le unmute
                return { ...prevMutedTracks, [speaker]: false };
            } else {
                // Si actuellement affiché comme non-muté, forcer le mute
                return { ...prevMutedTracks, [speaker]: true };
            }
        });
    }, [isVideoAudioEnabled]);


    const handleBlockSelect = useCallback((block) => {
        setSelectedBlock(block);
        if (block) {
            handleSeek(block.start);
        }
    }, [handleSeek]);

    // Gestionnaire pour recharger la waveform
    const handleWaveformReload = useCallback((speaker, updatedSegment) => {
        console.log("Reloading waveform for:", updatedSegment);
        // Mettre à jour le transcript directement
        setTranscript(prev => prev.map(segment =>
            (segment.start === updatedSegment.start && segment.speaker === speaker)
                ? updatedSegment
                : segment
        ));

        // Mettre aussi à jour audioSegments pour la cohérence
        setAudioSegments(prev => ({
            ...prev,
            [speaker]: prev[speaker].map(segment =>
                segment.start === updatedSegment.start ? updatedSegment : segment
            )
        }));
    }, []);

    // Nouveau gestionnaire pour recharger l'audio
    // Dans MainPage.js, modifiez handleAudioReload :
    const handleAudioReload = useCallback((speaker, updatedSegment) => {
        console.log(`Reloading audio for ${speaker}: ${updatedSegment.segment_file}`);

        if (audioPlayerRefs.current[speaker]) {
            // Utiliser la nouvelle méthode forceReloadSegment
            audioPlayerRefs.current[speaker].forceReloadSegment(updatedSegment);
        }

        // Mettre à jour audioSegments
        setAudioSegments(prev => ({
            ...prev,
            [speaker]: prev[speaker].map(segment =>
                segment.start === updatedSegment.start ? updatedSegment : segment
            )
        }));
    }, []);

    const handleVideoReload = useCallback((updatedSegment) => {
        console.log(`Reloading video for: ${updatedSegment.video_segment_file}`);

        if (videoPlayerRef.current?.forceReloadSegment) {
            videoPlayerRef.current.forceReloadSegment(updatedSegment);
        }
    }, []);

    const handleResetAudioCache = useCallback((speaker) => {
        console.log(speaker ? `Resetting cache for ${speaker}` : 'Resetting all caches');

        const wasPlaying = isPlaying;
        if (wasPlaying) {
            setIsPlaying(false);
        }

        // Reset audio players
        if (speaker && audioPlayerRefs.current[speaker]?.resetCache) {
            audioPlayerRefs.current[speaker].resetCache();
        } else {
            Object.values(audioPlayerRefs.current).forEach(player => {
                if (player?.resetCache) {
                    player.resetCache();
                }
            });
        }

        // ✅ NOUVEAU : Reset aussi le cache vidéo
        if (videoPlayerRef.current?.resetCache) {
            videoPlayerRef.current.resetCache();
        }

        if (wasPlaying) {
            setTimeout(() => {
                setIsPlaying(true);
            }, 1000);
        }
    }, [isPlaying]);

    const checkUndoAvailability = useCallback(async () => {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/project/undo_availability/${projectId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            setCanUndo(response.data.canUndo);
        } catch (error) {
            console.error('Error checking undo availability:', error);
            setCanUndo(false);
        }
    }, [projectId]);

    const handleSave = useCallback(async (transcriptToSave = null) => {
        try {
            // Utiliser le transcript passé en paramètre s'il existe, sinon utiliser le transcript du state
            const dataToSave = transcriptToSave || transcript;

            console.log("Saving transcript:", dataToSave); // Pour debug

            const response = await axios.post(`${API_CONFIG.BASE_URL}/update_transcript/${projectId}`, dataToSave, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.data?.new_url) {
                console.log("Transcript saved. New URL:", response.data.new_url);
            }

            setMessage('Transcript updated successfully');

            // Vérifier si l'annulation est disponible après la sauvegarde
            checkUndoAvailability();
        } catch (error) {
            setMessage('Error updating transcript: ' + (error.response?.data?.error || error.message));
        }
    }, [transcript, projectId, checkUndoAvailability]);

    const handleExport = useCallback(async (exportType = 'audio') => {
        setIsExporting(true);
        try {
            // Déterminer si on inclut les sous-titres (uniquement en export vidéo et s'ils sont activés)
            const includeSubtitles = exportType === 'video' && subtitleSettings.enabled;

            // Exporter avec le choix d'inclure ou non les sous-titres
            const blob = await projectService.exportAudio(
                projectId,
                exportType,
                includeSubtitles
            );

            // Télécharger le fichier
            const extension = exportType === 'video' ? 'mp4' : 'wav';
            projectService.downloadFile(blob, `project_${projectId}_export.${extension}`);

            // Message de confirmation avec indication des sous-titres si applicable
            if (exportType === 'video' && includeSubtitles) {
                setMessage(`Vidéo exportée avec succès (avec sous-titres intégrés).`);
            } else {
                setMessage(`${exportType === 'video' ? 'Vidéo' : 'Audio'} exporté avec succès.`);
            }
        } catch (error) {
            console.error(`Error exporting ${exportType}:`, error);
            setMessage(`Erreur lors de l'export: ${error.message}`);
        } finally {
            setIsExporting(false);
        }
    }, [projectId, subtitleSettings]);

    const toggleVideoAudio = useCallback(() => {
        const newVideoAudioState = !isVideoAudioEnabled;

        console.log(`Switching video audio to:`, newVideoAudioState); // Debug

        setIsVideoAudioEnabled(newVideoAudioState);

        if (videoRef.current) {
            videoRef.current.muted = !newVideoAudioState;
        }

        // ✅ CORRECTION : Ne pas manipuler directement les AudioPlayers
        // La prop muted va automatiquement être mise à jour dans le rendu
    }, [isVideoAudioEnabled]);

    // Supprimer ou modifier cet useEffect
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = !isVideoAudioEnabled;
        }
        // Supprimer la manipulation directe des audioRefs
    }, [isVideoAudioEnabled]);

    const handleTranscriptUpdate = useCallback((updatedTranscript) => {
        setTranscript(updatedTranscript);
    }, []);

    // Appelez cette fonction au chargement initial et après chaque opération
    useEffect(() => {
        if (projectId) {
            checkUndoAvailability();
        }
    }, [checkUndoAvailability, projectId]);

    // Ajoutez la fonction pour effectuer l'annulation
    const handleUndo = useCallback(async () => {
        if (!canUndo || isUndoing) return;

        setIsUndoing(true);
        setMessage('Undoing last operation...');

        try {
            console.log('🔄 Starting undo process...');
            setIsPlaying(false);
            window._undoInProgress = true;

            // Nettoyer audio ET vidéo
            console.log('🧹 Cleaning all caches...');
            Object.entries(audioPlayerRefs.current).forEach(([speaker, player]) => {
                if (player?.pause) player.pause();
                if (player?.resetCache) player.resetCache();
            });

            // ✅ NOUVEAU : Nettoyer aussi le cache vidéo
            if (videoPlayerRef.current?.resetCache) {
                videoPlayerRef.current.resetCache();
            }

            await new Promise(resolve => setTimeout(resolve, 500));

            const response = await axios.post(
                `${API_CONFIG.BASE_URL}/project/undo/${projectId}`,
                {},
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );

            if (response.data.restored_transcript) {
                console.log('✅ Undo API successful, updating transcript...');

                setTranscript(response.data.restored_transcript);

                const uniqueSpeakers = [...new Set(response.data.restored_transcript.map(item => item.speaker))];
                const segments = Object.fromEntries(uniqueSpeakers.map(speaker => [
                    speaker,
                    response.data.restored_transcript.filter(item => item.speaker === speaker)
                ]));
                setAudioSegments(segments);

                const newMutedTracks = Object.fromEntries(uniqueSpeakers.map(speaker => [speaker, false]));
                setMutedTracks(newMutedTracks);

                await new Promise(resolve => setTimeout(resolve, 1000));

                console.log('🎵 Restarting players with new transcript...');
                window._undoInProgress = false;

                // Restart audio players
                for (const [speaker, player] of Object.entries(audioPlayerRefs.current)) {
                    if (player?.resetCache) {
                        console.log(`🔄 Restarting ${speaker}...`);
                        player.resetCache();
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                }

                // ✅ NOUVEAU : Restart aussi le VideoPlayer
                if (videoPlayerRef.current?.resetCache) {
                    console.log('🎬 Restarting VideoPlayer...');
                    videoPlayerRef.current.resetCache();
                }

                setMessage(`Successfully undone ${response.data.operation_type} operation`);
            }

            checkUndoAvailability();

        } catch (error) {
            console.error('❌ Error during undo:', error);
            setMessage(`Error undoing operation: ${error.response?.data?.error || error.message}`);
        } finally {
            window._undoInProgress = false;
            setIsUndoing(false);
        }
    }, [projectId, canUndo, isUndoing, checkUndoAvailability]);

    // Ajoutez également cette fonction utilitaire pour nettoyer spécifiquement après undo
    const cleanupAfterUndo = useCallback(async () => {
        console.log('Performing post-undo cleanup...');

        // Arrêter tous les AudioPlayers
        Object.values(audioPlayerRefs.current).forEach(player => {
            if (player?.pause) {
                player.pause();
            }
        });

        // Nettoyer tous les caches
        Object.values(audioPlayerRefs.current).forEach(player => {
            if (player?.resetCache) {
                player.resetCache();
            }
        });

        // Réinitialiser l'état de lecture
        setIsPlaying(false);
        setCurrentTime(0);

        console.log('Post-undo cleanup completed');
    }, []);



    // Ajoutez un gestionnaire d'événements clavier pour Ctrl+Z
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Ne pas déclencher pendant l'édition de texte
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
                return;
            }

            if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                handleUndo();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleUndo]);

    const uniqueSpeakers = [...new Set(transcript.map(item => item.speaker))];

    const components = {
        subtitleManager: (
            <SubtitleManager
                projectId={projectId}
                onSettingsChange={setSubtitleSettings}
                onSubtitlesChange={setSubtitles}
                currentSettings={subtitleSettings}
            />
        ),
        lipSyncManager: (
            <LipSyncManager
                transcript={transcript}
                projectId={projectId}
                onTranscriptUpdate={handleTranscriptUpdate}
                onSave={handleSave}
                onProcessingChange={handleLipsyncProcessingChange}
                onVideoReload={handleVideoReload}
            />
        ),
        referenceManager: (
            <ReferenceFilesManager
                projectId={projectId}
                onFilesUpdate={setReferenceFiles}
            />
        ),
        transcriptViewer: (
            <TranscriptViewer
                transcript={transcript}
                currentTime={currentTime}
                onSeek={handleSeek}
                selectedBlock={selectedBlock}
                onBlockSelect={handleBlockSelect}
                onTranscriptUpdate={handleTranscriptUpdate}
                onSave={handleSave}
                projectId={projectId}
                onReloadWaveform={handleWaveformReload}
                onReloadAudio={handleAudioReload} // Nouveau prop
                referenceFiles={referenceFiles}
            />
        ),
        videoPlayer: (
            <VideoPlayer
                ref={videoPlayerRef}  // ✅ NOUVEAU : Ajouter la ref
                videoRef={videoRef}
                currentTime={currentTime}
                duration={duration}
                isPlaying={isPlaying}
                handlePlayPause={handlePlayPause}
                handleSkip={handleSkip}
                videoUrl={videoUrl}
                projectId={projectId}  // Ajout de projectId
                subtitleSettings={subtitleSettings}  // Ajout des paramètres de sous-titres
                subtitles={subtitles}  // Ajout des sous-titres
                transcript={transcript}  // Passer le transcript
            />
        ),
        timeline: (
            <Timeline
                transcript={transcript}
                currentTime={currentTime}
                duration={duration}
                onSeek={handleSeek}
                selectedBlock={selectedBlock}
                onBlockSelect={handleBlockSelect}
                onTranscriptUpdate={handleTranscriptUpdate}
                onSave={handleSave}
                speakers={uniqueSpeakers}
                mutedTracks={mutedTracks}
                toggleMute={toggleMute}
                projectId={projectId}
                isVideoAudioEnabled={isVideoAudioEnabled}
                toggleVideoAudio={toggleVideoAudio}
                onResetAudioCache={handleResetAudioCache}
                handleAudioReload={handleAudioReload}
                hasVideo={!!videoUrl}
                audioPlayerRefs={audioPlayerRefs}
                isPlaying={isPlaying}
                checkUndoAvailability={checkUndoAvailability} // Ajoutez cette prop
                subtitleSettings={subtitleSettings}
                onSubtitleSettingsChange={(newSettings) => {
                    setSubtitleSettings(newSettings);
                }}
                lipsyncProcessingInfo={lipsyncProcessingInfo}
            />
        ),
        volumeWidget: (
            <AudioVolumePanel
                selectedBlock={selectedBlock}
                transcript={transcript}
                onTranscriptUpdate={handleTranscriptUpdate}
                onSave={handleSave}
                speakerOrder={uniqueSpeakers}
                audioPlayerRefs={audioPlayerRefs}
            />
        ),
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {transcript.length > 0 && assetsLoaded ? (
                // Affichage normal avec le header quand le transcript est chargé
                <>
                    <Box sx={{ height: '60px', width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1000 }}>
                        <Header
                            onSave={handleSave}
                            onExport={handleExport}
                            isExporting={isExporting}
                            projectName={`Project ${projectId}`}
                            language="en"
                            hasVideo={!!videoUrl}
                            onUndo={handleUndo}
                            canUndo={canUndo}
                            isUndoing={isUndoing} />
                    </Box>
                    <Box sx={{ position: 'absolute', top: '60px', left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                        {message && (
                            <Box sx={{ p: 2, bgcolor: 'error.main', color: 'error.contrastText' }}>
                                {message}
                            </Box>
                        )}
                        <FlexibleLayout components={components} />
                        {/* Dans le JSX de MainPage.js */}
                        <Box sx={{ display: 'none' }}> {/* Caché car audio seulement */}
                            {uniqueSpeakers.map(speaker => (
                                <AudioPlayer
                                    key={`${speaker}-${projectId}`} // Clé stable et unique
                                    speakerId={speaker}
                                    transcript={transcript}
                                    isPlaying={isPlaying}
                                    currentTime={currentTime}
                                    muted={mutedTracks[speaker] || isVideoAudioEnabled}
                                    projectId={projectId}
                                    ref={el => {
                                        if (el) audioPlayerRefs.current[speaker] = el;
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </>
            ) : (
                // Écran de chargement en plein écran
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                    <LoadingScreen />
                </Box>
            )}
        </Box>
    );
}

export default MainPage;
